import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

export const Qualification = () => {
  const forYou = [
    "You run a roofing company with 5–50 employees",
    "You generate 30+ inbound leads per month",
    "You have a dedicated sales team or process",
    "You're serious about growing revenue, not just cutting costs",
    "You can make decisions quickly"
  ];

  const notForYou = [
    "You're a solo operator or just starting out",
    "You get fewer than 30 leads per month",
    "You don't have systems in place yet",
    "You're looking for the cheapest option available",
    "You need to consult 5 people before making any decision"
  ];

  const scrollToCta = () => {
    const element = document.getElementById('cta-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="qualification-section" className="py-20 md:py-28 bg-slate-50" data-testid="qualification-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Is This Right For You?
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
            We're Selective About Who We Work With
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
            This system delivers serious ROI for the right companies. Let's make sure we're a fit before either of us invests time.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* This Is For You */}
          <Card className="bg-white border-green-200 rounded-sm" data-testid="for-you-card">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900">
                  This Is For You If...
                </h3>
              </div>
              <ul className="space-y-4">
                {forYou.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* This Is NOT For You */}
          <Card className="bg-white border-red-200 rounded-sm" data-testid="not-for-you-card">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900">
                  This Is NOT For You If...
                </h3>
              </div>
              <ul className="space-y-4">
                {notForYou.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Investment Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <Card className="bg-white border-slate-200 rounded-sm" data-testid="investment-card">
            <CardContent className="p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                The Investment
              </h3>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 mb-6">
                <div>
                  <p className="text-slate-500 text-sm uppercase tracking-wider mb-1">Setup Fee</p>
                  <p className="font-heading text-4xl font-bold text-slate-900">$3,000</p>
                  <p className="text-slate-500 text-sm">One-time</p>
                </div>
                <div className="hidden sm:block w-px h-16 bg-slate-200"></div>
                <div>
                  <p className="text-slate-500 text-sm uppercase tracking-wider mb-1">Monthly</p>
                  <p className="font-heading text-4xl font-bold text-slate-900">$1,500</p>
                  <p className="text-slate-500 text-sm">Ongoing optimization & management</p>
                </div>
              </div>
              <p className="text-slate-600 mb-8 max-w-xl mx-auto">
                This includes full system setup, AI training for your business, calendar integration, ongoing optimization, and dedicated support. No hidden fees.
              </p>
              <Button 
                onClick={scrollToCta}
                className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-6 text-base rounded-sm active:scale-[0.98] transition-transform"
                data-testid="qualification-cta"
              >
                Apply for Strategy Call
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Qualification;
